import React, { useState, useEffect, FC } from "react";

export interface VatSelectorProps {
  initialValue?: number;
  presetValues?: number[];
  onChange: (value: number) => void;
  labelText?: string;
  descriptionText?: string;
}

export const VatSelector: FC<VatSelectorProps> = ({
  initialValue = 25,
  presetValues = [12, 15, 25],
  onChange,
  labelText,
  descriptionText,
}) => {
  // State to track active VAT type (preset button or custom input)
  const [activeVatType, setActiveVatType] = useState<"preset" | "custom">(
    presetValues.includes(initialValue) ? "preset" : "custom",
  );

  // State for the actual VAT value
  const [vatValue, setVatValue] = useState<number>(initialValue);

  // State for the visual value of the custom VAT input
  const [customVatDisplayValue, setCustomVatDisplayValue] = useState<string>(
    activeVatType === "custom" && initialValue !== 0
      ? String(initialValue)
      : "",
  );

  // Update internal state when initialValue prop changes
  useEffect(() => {
    const newVatType = presetValues.includes(initialValue)
      ? "preset"
      : "custom";
    setVatValue(initialValue);
    setActiveVatType(newVatType);
    setCustomVatDisplayValue(
      newVatType === "custom" && initialValue !== 0 ? String(initialValue) : "",
    );
  }, [initialValue, presetValues]);

  // Button handler for clicking a preset VAT value
  const handleVatButtonClick = (value: number) => {
    setVatValue(value);
    setActiveVatType("preset");
    setCustomVatDisplayValue(""); // Clear custom display when preset is clicked
    onChange(value); // Call parent callback with new value
  };

  // Input handler for custom VAT value
  const handleCustomVatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const displayValue = e.target.value;
    setCustomVatDisplayValue(displayValue); // Update display immediately

    let numValue = 0;
    if (displayValue === "") {
      numValue = 0;
    } else {
      const parsedValue = parseFloat(displayValue);
      // Allow valid numbers between 0 and 100
      if (!isNaN(parsedValue) && parsedValue >= 0 && parsedValue <= 100) {
        numValue = parsedValue;
        setVatValue(numValue);
        onChange(numValue); // Call parent callback with new value
      } else {
        // If invalid input (e.g., letters, >100), do not update the state
        return;
      }
    }
  };

  // Handle focus on custom input
  const handleCustomVatFocus = () => {
    setActiveVatType("custom");
    // When focusing, set the display value based on the current actual VAT
    setCustomVatDisplayValue(vatValue === 0 ? "" : String(vatValue));
  };

  return (
    <div>
      {labelText && (
        <label className="block mb-2 text-sm font-medium text-text">
          {labelText}
        </label>
      )}
      <div className="space-y-2">
        <div className="flex items-center">
          {/* Preset VAT Buttons */}
          <div className="flex space-x-2">
            {presetValues.map((value) => (
              <button
                key={value}
                type="button" // Prevent form submission
                className={`px-4 py-2 text-sm rounded-md ${
                  activeVatType === "preset" && vatValue === value
                    ? "bg-brand-muted text-brand border border-brand-muted font-medium dark:bg-lgb-blue-400 dark:text-white dark:border-lgb-blue-350"
                    : "bg-gray-100 text-gray-700 border border-gray-300 hover:bg-surface-muted dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                }`}
                onClick={() => handleVatButtonClick(value)}
              >
                {value}%
              </button>
            ))}
          </div>

          {/* Custom VAT Input */}
          <div className="relative w-20 ml-3">
            <input
              id="customVat"
              name="customVat"
              type="number"
              min="0"
              max="100"
              step="any" // Allow decimals
              className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-brand focus:border-brand block w-full py-2 px-2 pr-6 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-lgb-blue-350 dark:focus:border-lgb-blue-350"
              placeholder=""
              value={customVatDisplayValue}
              onChange={handleCustomVatChange}
              onFocus={handleCustomVatFocus}
            />
            <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500">
              %
            </span>
          </div>
        </div>
        {descriptionText && (
          <p className="mt-2 text-sm text-muted">
            {descriptionText}
          </p>
        )}
      </div>
    </div>
  );
};

export default VatSelector;
